import { FC, HTMLAttributes, ReactNode } from "react";
import clsx from "clsx";
import Image from "next/image";

import HtmlText from "@/sections/common/HtmlText";
import SectionHeader from "@/sections/common/SectionHeader";
import icon, { IIconName } from "@/utils/icon.helper";

// ----------------------------------------------------------------------------
// Services / benefits section — bento (asymmetric) grid.
//
// 4 cards arranged as 7+5 / 5+7 on lg+, each with a different brand
// accent so the section reads as visually rich rather than a uniform
// feature grid. Falls back to a 2-col grid on tablet, 1-col on mobile.
//
// Strapi block API unchanged (heading + description + cards[]).
// ----------------------------------------------------------------------------

export type ICard = {
  icon?: IIconName;
  heading?: string;
  description?: ReactNode;
  variant?: "primary" | "secondary" | "white" | "light_primary";
  size?: "xsm" | "sm" | "regular" | "md" | "lg" | "xlg";
};

export type IServicesSectionProps = HTMLAttributes<HTMLElement> & {
  heading?: string;
  description?: ReactNode;
  variant?: "primary" | "secondary" | "white";
  cards?: ICard[];
};

// Section background.
const sectionBg = {
  primary: "bg-primary-50",
  secondary: "bg-secondary-50",
  white: "bg-white",
};

// Bento positions for up to 4 cards (12-col grid on lg+).
// Asymmetric 7+5 / 5+7 layout — no two adjacent cards are the same width.
const BENTO_LAYOUT: readonly string[] = [
  "lg:col-span-7", // 0 — wide top-left
  "lg:col-span-5", // 1 — narrow top-right
  "lg:col-span-5", // 2 — narrow bottom-left
  "lg:col-span-7", // 3 — wide bottom-right
];

// Per-card accent styling — light tints across the board so the
// multi-color brand icons (orange + blue) stay visible on every card.
// No dark gradients here; the asymmetric bento layout carries the
// visual interest, the soft accents add variety.
type Accent = {
  card: string;
  iconBadge: string;
  decor: string;
};
const ACCENTS: readonly Accent[] = [
  // 0 — primary tint
  {
    card: "bg-primary-50 border-primary-100",
    iconBadge: "bg-white shadow-sm",
    decor: "bg-primary-900/15",
  },
  // 1 — yellow accent
  {
    card: "bg-yellow-50 border-yellow-200",
    iconBadge: "bg-white shadow-sm",
    decor: "bg-yellow-400/30",
  },
  // 2 — secondary tint
  {
    card: "bg-secondary-50 border-secondary-100",
    iconBadge: "bg-white shadow-sm",
    decor: "bg-secondary-900/15",
  },
  // 3 — clean white with primary accent
  {
    card: "bg-white border-black-100",
    iconBadge: "bg-primary-50",
    decor: "bg-primary-900/10",
  },
];

const ServicesSection: FC<IServicesSectionProps> = ({
  heading,
  description,
  variant = "white",
  cards,
  className,
  ...other
}) => {
  return (
    <section
      className={clsx(
        sectionBg[variant],
        "py-10 md:py-14 lg:py-16 xl:py-20",
        className,
      )}
      {...other}
    >
      <div className="container">
        {(heading || description) && (
          <SectionHeader
            title={heading}
            subtitle={description}
            align="center"
            tone="light"
            className="mb-10 md:mb-12 lg:mb-14"
          />
        )}

        {cards && cards.length > 0 && (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-12 lg:gap-6">
            {cards.map((card, idx) => {
              const layout = BENTO_LAYOUT[idx] ?? "lg:col-span-6";
              const accent = ACCENTS[idx % ACCENTS.length];
              return (
                <article
                  key={idx}
                  className={clsx(
                    "group relative isolate flex h-full min-h-[260px] flex-col overflow-hidden rounded-2xl border p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl md:min-h-[280px] md:p-8",
                    layout,
                    accent.card,
                  )}
                >
                  {/* Decorative blurred blob in the top-right of each card
                      — pulls from the accent palette so each card has a
                      slightly different visual signature without
                      obscuring the icon's brand colors. */}
                  <div
                    aria-hidden="true"
                    className={clsx(
                      "pointer-events-none absolute -right-12 -top-12 -z-10 h-40 w-40 rounded-full opacity-60 blur-3xl",
                      accent.decor,
                    )}
                  />

                  {/* Icon badge — white background so the multi-color
                      brand icons (orange + blue) render at full fidelity
                      like the original section. Size matches the original
                      responsive ladder (32 → 40 → 50px). */}
                  {card.icon && (
                    <div
                      className={clsx(
                        "mb-6 inline-flex h-16 w-16 items-center justify-center rounded-2xl md:h-[72px] md:w-[72px]",
                        accent.iconBadge,
                      )}
                    >
                      <Image
                        src={icon(card.icon) ?? ""}
                        alt=""
                        width={50}
                        height={50}
                        className="size-8 md:size-10 xl:size-[50px]"
                      />
                    </div>
                  )}

                  {card.heading && (
                    <h3
                      className={clsx(
                        // Light weight (300) for a modern, editorial feel
                        // — pairs better with the Bricolage Grotesque
                        // letterforms at heading sizes than semibold.
                        "mb-3 font-bricolage-grotesque font-extralight !leading-[1.2] tracking-tight text-black-950",
                        // Wider (col-span-7) cards get a bigger heading
                        // since they have more horizontal room.
                        idx === 0 || idx === 3
                          ? "text-[clamp(1.25rem,1vw+1rem,1.75rem)]"
                          : "text-[clamp(1.125rem,0.75vw+0.875rem,1.5rem)]",
                      )}
                    >
                      {card.heading}
                    </h3>
                  )}

                  {card.description && (
                    // Responsive weight — normal on mobile for legibility,
                    // light on lg+ for the editorial look.
                    <div className="text-[clamp(0.875rem,0.3vw+0.8rem,1rem)] font-light leading-relaxed text-black-700 lg:font-extralight">
                      <HtmlText className="prose-p:!text-[clamp(0.875rem,0.3vw+0.8rem,1rem)] prose-p:!font-light prose-p:!text-black-700 lg:prose-p:!font-extralight">
                        {card.description}
                      </HtmlText>
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

export default ServicesSection;
